import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PermMedia as MediaIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import TeacherLayout from "../components/TeacherLayout";
import { useTeacherAuth } from "../hooks/useTeacherAuth";
import { teacherPortalAPI } from "../utils/api";
import type {
  TeacherChild,
  TeacherClassroom,
  TeacherEventRequest,
  TeacherEventType,
  TeacherNurseryEvent,
} from "../types/teacher";

const eventTypes: TeacherEventType[] = [
  "birthday",
  "nursery_event",
  "graduation",
  "holiday",
  "field_trip",
  "special_event",
];
const typeColors: Record<TeacherEventType, string> = {
  birthday: "#F8DE8A",
  nursery_event: "#A7E5DE",
  graduation: "#BDE7D7",
  holiday: "#F5C56E",
  field_trip: "#F6B6BB",
  special_event: "#D8C7F7",
};
const emptyForm = (): TeacherEventRequest => ({
  type: "birthday",
  title: "",
  date: new Date().toISOString().slice(0, 10),
});
const scheduleFields: (keyof TeacherEventRequest)[] = [
  "date",
  "classroom_id",
  "child_id",
  "start_time",
  "end_time",
];
const errorMessage = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (data?.errors) return Object.values(data.errors).flat().join(" ");
  return data?.message || err?.message || fallback;
};
const plusThirtyMinutes = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  return `${String(Math.min(23, Math.floor(Math.min(hours * 60 + minutes + 30, 1439) / 60))).padStart(2, "0")}:${String(Math.min(hours * 60 + minutes + 30, 1439) % 60).padStart(2, "0")}`;
};

const TeacherEvents: React.FC = () => {
  const { t } = useTranslation("pages", { keyPrefix: "teacherEvents" });
  const { teacher } = useTeacherAuth();
  const [events, setEvents] = useState<TeacherNurseryEvent[]>([]);
  const [form, setForm] = useState<TeacherEventRequest>(emptyForm());
  const [editing, setEditing] = useState<TeacherNurseryEvent | null>(null);
  const [mediaEvent, setMediaEvent] = useState<TeacherNurseryEvent | null>(
    null,
  );
  const [classrooms, setClassrooms] = useState<TeacherClassroom[]>([]);
  const [children, setChildren] = useState<TeacherChild[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, string>>(
    {},
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [classroomsLoading, setClassroomsLoading] = useState(false);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [childrenLoadFailed, setChildrenLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const childrenRequestRef = useRef(0);
  const ownsEvent = (event: TeacherNurseryEvent) =>
    event.teacher_id === teacher?.id;
  const canEditEvent = (event: TeacherNurseryEvent) =>
    ownsEvent(event) && event.can_edit !== false;
  const canDeleteEvent = (event: TeacherNurseryEvent) =>
    ownsEvent(event) && event.can_delete !== false;

  const loadEvents = useCallback(
    async (quiet = false) => {
      if (!quiet) {
        setLoading(true);
        setError(null);
      }
      try {
        const response = await teacherPortalAPI.getEvents();
        const list = response.events || response.data || [];
        setEvents(list);
        setMediaEvent((current) =>
          current
            ? list.find((item) => item.id === current.id) || current
            : null,
        );
        return list;
      } catch (err: any) {
        if (!quiet) setError(errorMessage(err, t("loadFailed")));
        else console.warn("Event polling failed", err);
        return null;
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);
  useEffect(() => {
    if (!mediaEvent || mediaEvent.auto_attach_status !== "in_progress") return;
    const timer = window.setInterval(() => {
      void loadEvents(true);
    }, 30000);
    return () => window.clearInterval(timer);
  }, [mediaEvent?.id, mediaEvent?.auto_attach_status, loadEvents]);

  const loadClassrooms = useCallback(async () => {
    setClassroomsLoading(true);
    try {
      const response = await teacherPortalAPI.getClassrooms();
      setClassrooms(response.classrooms || response.data || []);
    } catch (err: any) {
      setDialogError(errorMessage(err, t("loadClassroomsFailed")));
    } finally {
      setClassroomsLoading(false);
    }
  }, [t]);
  const loadChildren = useCallback(
    async (classroomId: number) => {
      const requestId = ++childrenRequestRef.current;
      setChildrenLoading(true);
      setChildrenLoadFailed(false);
      try {
        const response =
          await teacherPortalAPI.getClassroomChildren(classroomId);
        if (requestId === childrenRequestRef.current)
          setChildren(response.children || response.data || []);
      } catch (err: any) {
        if (requestId === childrenRequestRef.current) {
          setChildren([]);
          setChildrenLoadFailed(true);
          setDialogError(errorMessage(err, t("loadChildrenFailed")));
        }
      } finally {
        if (requestId === childrenRequestRef.current) setChildrenLoading(false);
      }
    },
    [t],
  );

  const resetChildren = () => {
    childrenRequestRef.current += 1;
    setChildren([]);
    setChildrenLoading(false);
    setChildrenLoadFailed(false);
  };
  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    resetChildren();
    setDialogError(null);
    setDialogOpen(true);
    void loadClassrooms();
  };
  const openEdit = (event: TeacherNurseryEvent) => {
    setEditing(event);
    setDialogError(null);
    setForm({
      type: event.type,
      title: event.title,
      date: event.date.slice(0, 10),
      classroom_id: event.classroom_id,
      child_id: event.child_id ?? null,
      start_time: event.start_time ?? null,
      end_time: event.end_time ?? null,
    });
    setDialogOpen(true);
    void loadClassrooms();
    if (event.classroom_id) void loadChildren(event.classroom_id);
    else resetChildren();
  };
  const setClassroom = (value: string) => {
    const id = value ? Number(value) : null;
    setForm((current) => ({ ...current, classroom_id: id, child_id: null }));
    resetChildren();
    setDialogError(null);
    if (id) void loadChildren(id);
  };
  const setStartTime = (value: string) =>
    setForm((current) => ({
      ...current,
      start_time: value || null,
      end_time:
        value && !current.end_time
          ? plusThirtyMinutes(value)
          : current.end_time,
    }));
  const validationError = useMemo(() => {
    if (form.end_time && !form.start_time)
      return t("validation.endWithoutStart");
    if (form.start_time && !form.classroom_id)
      return t("validation.timeWithoutClassroom");
    if (form.child_id && !form.classroom_id)
      return t("validation.childWithoutClassroom");
    if (form.start_time && form.end_time && form.end_time <= form.start_time)
      return t("validation.endAfterStart");
    return null;
  }, [form, t]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setDialogError(null);
    if (validationError) {
      setDialogError(validationError);
      return;
    }
    if (editing) {
      const existingSchedule: Partial<TeacherEventRequest> = {
        date: editing.date.slice(0, 10),
        classroom_id: editing.classroom_id,
        child_id: editing.child_id,
        start_time: editing.start_time,
        end_time: editing.end_time,
      };
      const changed = scheduleFields.some(
        (key) => (form[key] ?? null) !== (existingSchedule[key] ?? null),
      );
      const hasCaptureHistory =
        editing.media.some((media) => media.source === "auto") ||
        ["in_progress", "completed", "failed"].includes(
          editing.auto_attach_status || "",
        );
      if (
        changed &&
        hasCaptureHistory &&
        !window.confirm(t("scheduleChangeConfirm"))
      )
        return;
    }
    setSaving(true);
    try {
      const payload: TeacherEventRequest = {
        type: form.type,
        title: form.title,
        date: form.date,
      };
      if (editing) {
        payload.classroom_id = form.classroom_id || null;
        payload.child_id = form.child_id || null;
        payload.start_time = form.start_time || null;
        payload.end_time = form.end_time || null;
      } else {
        if (form.classroom_id) payload.classroom_id = form.classroom_id;
        if (form.child_id) payload.child_id = form.child_id;
        if (form.start_time) payload.start_time = form.start_time;
        if (form.end_time) payload.end_time = form.end_time;
      }
      if (editing) await teacherPortalAPI.updateEvent(editing.id, payload);
      else await teacherPortalAPI.createEvent(payload);
      setDialogOpen(false);
      await loadEvents();
    } catch (err: any) {
      setDialogError(errorMessage(err, t("saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (event: TeacherNurseryEvent) => {
    if (!window.confirm(t("deleteConfirm"))) return;
    setSaving(true);
    try {
      await teacherPortalAPI.deleteEvent(event.id);
      await loadEvents();
    } catch (err: any) {
      setError(errorMessage(err, t("deleteFailed")));
    } finally {
      setSaving(false);
    }
  };
  const uploadMedia = async () => {
    if (!mediaEvent || !selectedFiles.length) return;
    setUploading(true);
    setUploadingFiles(
      Object.fromEntries(selectedFiles.map((file) => [file.name, "0%"])),
    );
    try {
      await teacherPortalAPI.uploadEventMedia(
        mediaEvent.id,
        selectedFiles,
        (progress) => {
          const total =
            progress.total ||
            selectedFiles.reduce((sum, file) => sum + file.size, 0);
          const percent = total
            ? Math.min(100, Math.round((progress.loaded / total) * 100))
            : 0;
          setUploadingFiles((current) =>
            Object.fromEntries(
              Object.keys(current).map((name) => [name, `${percent}%`]),
            ),
          );
        },
      );
      setSelectedFiles([]);
      setUploadingFiles({});
      await loadEvents(true);
    } catch (err: any) {
      setError(errorMessage(err, t("uploadFailed")));
      setUploadingFiles(
        Object.fromEntries(
          selectedFiles.map((file) => [file.name, t("failed")]),
        ),
      );
    } finally {
      setUploading(false);
    }
  };
  const deleteMedia = async (mediaId: number) => {
    if (!mediaEvent) return;
    try {
      await teacherPortalAPI.deleteEventMedia(mediaEvent.id, mediaId);
      setMediaEvent((current) =>
        current
          ? {
              ...current,
              media: current.media.filter((media) => media.id !== mediaId),
            }
          : current,
      );
      await loadEvents(true);
    } catch (err: any) {
      if (err?.response?.status === 404) await loadEvents(true);
      else setError(errorMessage(err, t("deleteMediaFailed")));
    }
  };
  const openMedia = (event: TeacherNurseryEvent) => {
    setSelectedFiles([]);
    setUploadingFiles({});
    setMediaEvent(event);
  };
  const closeMedia = () => {
    if (uploading) return;
    setMediaEvent(null);
    setSelectedFiles([]);
    setUploadingFiles({});
  };
  const statusChip = (event: TeacherNurseryEvent) => {
    const status = event.auto_attach_status || "skipped";
    const colors: Record<string, "default" | "info" | "success" | "error"> = {
      pending: "info",
      in_progress: "success",
      completed: "success",
      skipped: "default",
      failed: "error",
    };
    const chip = (
      <Chip
        size="small"
        color={colors[status]}
        label={t(`statuses.${status}`)}
      />
    );
    return status === "failed" && event.auto_attach_error ? (
      <Tooltip title={event.auto_attach_error}>{chip}</Tooltip>
    ) : (
      chip
    );
  };

  return (
    <TeacherLayout title={t("title")}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          {t("title")}
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={openCreate}
          variant="contained"
        >
          {t("createEvent")}
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper sx={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("date")}</TableCell>
              <TableCell>{t("type")}</TableCell>
              <TableCell>{t("titleColumn")}</TableCell>
              <TableCell>{t("classroomChild")}</TableCell>
              <TableCell>{t("timeWindow")}</TableCell>
              <TableCell>{t("status")}</TableCell>
              <TableCell align="right">{t("actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event.id}>
                <TableCell>
                  {new Date(event.date).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`types.${event.type}`)}
                    sx={{ bgcolor: typeColors[event.type], fontWeight: 800 }}
                  />
                </TableCell>
                <TableCell>{event.title}</TableCell>
                <TableCell>
                  {event.classroom?.name || "-"}
                  {event.child?.name && (
                    <Typography variant="caption" display="block">
                      {event.child.name}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {event.start_time
                    ? `${event.start_time}${event.end_time ? `–${event.end_time}` : ""}`
                    : "-"}
                </TableCell>
                <TableCell>{statusChip(event)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    disabled={!canEditEvent(event) || saving}
                    onClick={() => openEdit(event)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    disabled={!canDeleteEvent(event) || saving}
                    onClick={() => deleteEvent(event)}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton
                    disabled={saving || uploading}
                    onClick={() => openMedia(event)}
                  >
                    <MediaIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && !events.length && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t("noEvents")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={submit}>
          <DialogTitle>
            {editing ? t("editEvent") : t("createEvent")}
          </DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {dialogError}
              </Alert>
            )}
            <TextField
              fullWidth
              select
              label={t("type")}
              margin="normal"
              value={form.type}
              onChange={(e) =>
                setForm({ ...form, type: e.target.value as TeacherEventType })
              }
            >
              {eventTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`types.${type}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              required
              label={t("eventTitle")}
              margin="normal"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <TextField
              fullWidth
              required
              type="date"
              label={t("eventDate")}
              margin="normal"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              select
              label={t("classroom")}
              margin="normal"
              value={form.classroom_id || ""}
              disabled={classroomsLoading}
              onChange={(e) => setClassroom(e.target.value)}
            >
              <MenuItem value="">{t("none")}</MenuItem>
              {classrooms.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              select
              label={t("child")}
              margin="normal"
              value={form.child_id || ""}
              disabled={
                !form.classroom_id || childrenLoading || childrenLoadFailed
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  child_id: e.target.value ? Number(e.target.value) : null,
                })
              }
            >
              <MenuItem value="">{t("none")}</MenuItem>
              {children.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="time"
                  label={t("startTime")}
                  margin="normal"
                  value={form.start_time || ""}
                  onChange={(e) => setStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="time"
                  label={t("endTime")}
                  margin="normal"
                  value={form.end_time || ""}
                  onChange={(e) =>
                    setForm({ ...form, end_time: e.target.value || null })
                  }
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    form.start_time && !form.end_time
                      ? t("defaultEndTime")
                      : undefined
                  }
                />
              </Grid>
            </Grid>
            <Alert
              severity={
                form.classroom_id && form.start_time ? "success" : "info"
              }
              sx={{ mt: 2 }}
            >
              {form.classroom_id && form.start_time
                ? t("eligibleHint")
                : t("calendarOnlyHint")}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t("captureHelper")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{t("cancel")}</Button>
            <Button type="submit" disabled={saving} variant="contained">
              {t("save")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(mediaEvent)}
        onClose={closeMedia}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t("manageMedia")}</DialogTitle>
        <DialogContent>
          {mediaEvent?.auto_attach_status === "in_progress" &&
            !mediaEvent.media.length && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t("waitingForPhotos")}
              </Alert>
            )}
          {mediaEvent?.auto_attach_status === "completed" &&
            !mediaEvent.media.length && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t("noMedia")}
              </Typography>
            )}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {mediaEvent?.media.map((media) => (
              <Grid key={media.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper sx={{ p: 1.5 }}>
                  <Chip
                    size="small"
                    label={t(media.source === "auto" ? "automatic" : "manual")}
                    sx={{ mb: 1 }}
                  />
                  {media.media_type === "video" ? (
                    <video
                      src={media.url ?? `${import.meta.env.VITE_API_URL}/${media.file_path}`}
                      controls
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={media.url ?? `${import.meta.env.VITE_API_URL}/${media.file_path}`}
                      alt={media.original_filename}
                      sx={{
                        width: "100%",
                        aspectRatio: "4/3",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  {mediaEvent && canEditEvent(mediaEvent) && (
                    <Button
                      color="error"
                      size="small"
                      onClick={() => deleteMedia(media.id)}
                    >
                      {t("deleteMedia")}
                    </Button>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
          {mediaEvent && canEditEvent(mediaEvent) && (
            <>
              <Button component="label" variant="outlined" disabled={uploading}>
                {t("selectFiles")}
                <input
                  hidden
                  multiple
                  type="file"
                  accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
                  onChange={(e) =>
                    setSelectedFiles(Array.from(e.target.files || []))
                  }
                />
              </Button>
              <Box sx={{ mt: 2 }}>
                {selectedFiles.map((file) => (
                  <Typography key={file.name}>
                    {file.name}: {uploadingFiles[file.name] || t("ready")}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMedia} disabled={uploading}>
            {t("close")}
          </Button>
          {mediaEvent && canEditEvent(mediaEvent) && (
            <Button
              onClick={uploadMedia}
              disabled={!selectedFiles.length || uploading}
              variant="contained"
            >
              {t("upload")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </TeacherLayout>
  );
};
export default TeacherEvents;
